/**
 * Susunan track lembar korelasi.
 *
 * Sampai versi 2.21 lembar korelasi menggambar kurvanya sendiri: sebuah SVG
 * yang ditulis di dalam layar, warna dari tabel tetap per mnemonic, arsiran
 * dari tabel tetap kedua, dan satu-satunya setelan yang bisa disentuh pengguna
 * adalah warna garis lewat pemilih warna di rel kanan. Lembar log — layar yang
 * sama-sama menggambar kurva pada sumbu kedalaman — sudah lama tidak begitu:
 * di sana setiap track adalah data, kepalanya bisa diklik, dan warna, bentuk
 * garis, ketebalan, kedua ujung skala serta arsirannya disetel di tempat kurva
 * itu sedang dipandang.
 *
 * Modul ini yang membuat keduanya menjadi satu barang. Yang tersimpan hanyalah
 * simpangan dari bawaan — satu entri per mnemonic — dan yang dibangkitkan di
 * sini adalah `TrackSpec` yang sama persis dengan yang dipakai lembar log,
 * sehingga komponen yang menggambarnya pun sama. Aritmetikanya dipisahkan dari
 * React supaya bisa diuji tanpa DOM: yang mudah keliru di sini adalah bagaimana
 * "belum disetel" dibedakan dari "sengaja dimatikan", dan itu tidak memerlukan
 * layar untuk salah.
 */

import type { TrackCurve, TrackSpec } from "../components/LogTracks";
import type { PanelCurve, PanelWell } from "../types";
import {
  defaultCurveColor,
  resolveAxisRange,
  suggestedScale,
  type CurveConfig,
  type CurveFill,
  type CurveStyle,
  type FillAnchor,
  type SpectrumConfig,
} from "./tracklayout";

/**
 * Setelan satu mnemonic di lembar korelasi.
 *
 * Bentuknya `CurveConfig` lembar log dengan dua medan yang boleh bernilai
 * `null`, dan `null` di sini berarti sesuatu yang berbeda dari kosong: kosong
 * adalah "belum disetel, pakai bawaan mnemonic ini", `null` adalah "sudah
 * disetel, dan yang diminta adalah tidak ada". Perbedaannya harus tersimpan.
 * `undefined` hilang ketika susunan ini ditulis sebagai JSON, jadi mematikan
 * arsiran GR dengan membiarkan medannya kosong berarti arsiran itu kembali
 * dengan sendirinya pada kunjungan berikutnya — sebuah setelan yang tidak
 * menurut, dan tidak ada yang bisa dilakukan penggunanya soal itu.
 */
export interface CorrCurveConfig extends Partial<Omit<CurveConfig, "fill">> {
  fill?: CurveFill | null;
  spectrum?: SpectrumConfig | null;
  /** Sumbu mendatar. Kosong berarti mengikuti kelaziman mnemonic-nya. */
  logarithmic?: boolean;
}

export type CorrCurveStore = Record<string, CorrCurveConfig>;

/** Kuning pasir lembar korelasi cetak — lebih pekat daripada `--litho-sand`. */
export const CORR_SAND_YELLOW = "#f2c500";

/**
 * Arsiran bawaan per mnemonic — sisi mana dari skala yang diisi.
 *
 * Ini penerus tabel `SHADE` yang dulu ada di dalam layar korelasi, dinyatakan
 * ulang dengan kosakata lembar log: bukan "pecahan lebar track" melainkan ujung
 * skala kurvanya sendiri. Bedanya bukan gaya penulisan. Sebuah garis dasar pada
 * 0,4 lebar track tetap berada di 0,4 ketika skalanya diubah, dibalik, atau
 * dijadikan logaritmik — dan lalu mengarsir setengah track yang berbeda dari
 * yang diminta. Ujung skala tidak bisa meleset begitu.
 */
export const CORR_DEFAULT_FILL: Record<string, FillAnchor | CurveFill> = {
  // GR diarsir di kedua sisinya, seperti lembar korelasi cetak: kuning di
  // sisi pasir — dari nol sampai kurvanya — dan abu-abu di sisi serpih.
  GR: { from: "min", color: CORR_SAND_YELLOW, opposite: "var(--litho-shale)" },
  VSH: "max",
  // RT diarsir dari ujung rendahnya sampai kurva, dengan warna yang menyapu
  // lebar tracknya: ungu–biru di resistivitas rendah (air), jingga–merah di
  // yang tinggi (hidrokarbon). "Across" dan bukan "value" dengan sengaja —
  // sumbunya logaritmik, dan warna menurut nilai linear membuat 0,2 sampai
  // 200 ohm·m satu warna yang sama; menurut kedudukan, warnanya mengikuti
  // dekade yang terbaca di skalanya.
  RT: { from: "min", ramp: "pt_rainbow" },
  PHIE: "min",
  SW: "min",
};

/**
 * Mnemonic yang bawaannya digambar sebagai pita warna, bukan garis.
 *
 * Permeabilitas dibaca pada dekade dan direntang lima dekade; sebagai garis ia
 * menempel di tepi kiri track sepanjang seluruh lubang kecuali di reservoir.
 * Sebagai warna, susunan lapisannya terbaca sekaligus — dan itulah yang selalu
 * dilakukan lembar korelasi terhadap kurva ini.
 */
export const CORR_DEFAULT_SPECTRUM: Record<string, SpectrumConfig> = {
  PERM: { ramp: "spectrum", bars: true },
};

const STYLES = new Set<CurveStyle>(["solid", "dashed", "dotted", "dashdot"]);

function numberOr(value: unknown, min?: number, max?: number): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  if (min !== undefined && value < min) return min;
  if (max !== undefined && value > max) return max;
  return value;
}

/** Arsiran yang tersimpan. `null` dipertahankan; ia sebuah jawaban, bukan kekosongan. */
function readFill(value: unknown): CurveFill | null | undefined {
  if (value === null) return null;
  if (!value || typeof value !== "object") return undefined;
  const fill = value as Record<string, unknown>;
  if (fill.from !== "min" && fill.from !== "max") return undefined;
  return {
    from: fill.from,
    color: typeof fill.color === "string" && fill.color ? fill.color : undefined,
    ramp: typeof fill.ramp === "string" && fill.ramp ? fill.ramp : undefined,
    reverse: fill.reverse === true ? true : undefined,
    by: fill.by === "value" ? "value" : undefined,
    opposite: typeof fill.opposite === "string" && fill.opposite ? fill.opposite : undefined,
  };
}

function readSpectrum(value: unknown): SpectrumConfig | null | undefined {
  if (value === null) return null;
  if (!value || typeof value !== "object") return undefined;
  const source = value as Record<string, unknown>;
  if (typeof source.ramp !== "string" || !source.ramp) return undefined;
  return {
    ramp: source.ramp,
    reverse: source.reverse === true || undefined,
    min: numberOr(source.min),
    max: numberOr(source.max),
    bars: source.bars === true || undefined,
  };
}

/**
 * Membaca kembali setelan yang tersimpan di peramban.
 *
 * Kuncinya sama dengan kunci yang dulu menyimpan warna saja, dan bentuk lama
 * itu — `{"GR": "#7fc06a"}` — masih terbaca sebagai warna. Membuang setelan
 * lama dan memulai dari kosong berarti seorang pengguna yang sudah menyetel
 * warna lembarnya membuka layar ini dan menemukan lembarnya kembali ke bawaan,
 * tanpa satu pun kata yang menjelaskan ke mana perginya.
 */
export function normalizeCorrCurves(raw: unknown): CorrCurveStore {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const store: CorrCurveStore = {};
  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!name) continue;
    if (typeof value === "string") {
      if (value) store[name] = { color: value };
      continue;
    }
    if (!value || typeof value !== "object") continue;
    const source = value as Record<string, unknown>;
    const entry: CorrCurveConfig = {
      color: typeof source.color === "string" && source.color ? source.color : undefined,
      style:
        typeof source.style === "string" && STYLES.has(source.style as CurveStyle)
          ? (source.style as CurveStyle)
          : undefined,
      width: numberOr(source.width, 0.4, 6),
      min: numberOr(source.min),
      max: numberOr(source.max),
      logarithmic: typeof source.logarithmic === "boolean" ? source.logarithmic : undefined,
      fill: readFill(source.fill),
      spectrum: readSpectrum(source.spectrum),
    };
    store[name] = entry;
  }
  return store;
}

/**
 * Menuliskan satu suntingan dari kepala track ke dalam susunan.
 *
 * `fill: undefined` yang datang dari tombol "Tanpa" disimpan sebagai `null`;
 * lihat alasannya pada `CorrCurveConfig`. Medan lain yang datang kosong memang
 * berarti kosong — tombol Auto mengirim `min` dan `max` kosong, dan yang
 * dimintanya justru "kembalikan ke skala tampilan kurvanya".
 */
export function patchCorrCurve(
  store: CorrCurveStore,
  name: string,
  patch: Partial<CurveConfig>,
): CorrCurveStore {
  const next: CorrCurveConfig = { ...(store[name] ?? {}), ...patch };
  if ("fill" in patch && patch.fill === undefined) next.fill = null;
  return { ...store, [name]: next };
}

/** Setelan pita warna satu mnemonic, disunting dari bar warna di kepalanya. */
export function patchCorrSpectrum(
  store: CorrCurveStore,
  name: string,
  patch: Partial<SpectrumConfig>,
): CorrCurveStore {
  const current = resolveSpectrum(name, store[name]) ?? { ramp: "spectrum" };
  return { ...store, [name]: { ...(store[name] ?? {}), spectrum: { ...current, ...patch } } };
}

/** Melepas pita warna sebuah mnemonic — ia kembali digambar sebagai garis. */
export function removeCorrSpectrum(store: CorrCurveStore, name: string): CorrCurveStore {
  return { ...store, [name]: { ...(store[name] ?? {}), spectrum: null } };
}

/** Menukar sumbu mendatar sebuah mnemonic antara linear dan logaritmik. */
export function toggleCorrScale(store: CorrCurveStore, name: string): CorrCurveStore {
  return {
    ...store,
    [name]: { ...(store[name] ?? {}), logarithmic: !resolveLogarithmic(name, store[name]) },
  };
}

export function resolveFill(name: string, config?: CorrCurveConfig): CurveFill | undefined {
  if (config?.fill === null) return undefined;
  if (config?.fill) return config.fill;
  const bawaan = CORR_DEFAULT_FILL[name];
  if (!bawaan) return undefined;
  return typeof bawaan === "string" ? { from: bawaan } : { ...bawaan };
}

export function resolveSpectrum(name: string, config?: CorrCurveConfig): SpectrumConfig | undefined {
  if (config?.spectrum === null) return undefined;
  return config?.spectrum ?? CORR_DEFAULT_SPECTRUM[name];
}

export function resolveLogarithmic(name: string, config?: CorrCurveConfig): boolean {
  return config?.logarithmic ?? suggestedScale(name) === "log";
}

/** Warna garis sebuah mnemonic: pilihan pengguna, lalu bawaan tema. */
export function resolveCorrColor(name: string, config?: CorrCurveConfig, stored?: string): string {
  return config?.color ?? defaultCurveColor(name, stored);
}

/**
 * Satu track lembar korelasi, dalam bentuk yang dimengerti komponen lembar log.
 *
 * Yang berbeda dari lembar log hanyalah dari mana kurvanya datang. Di sana satu
 * track membawa beberapa mnemonic dari satu sumur; di sini satu track membawa
 * satu mnemonic dari beberapa sumur yang ditumpuk dalam satu panel — dan pada
 * panel bertumpuk yang membedakan garis bukan lagi namanya melainkan sumurnya,
 * jadi itu pula yang menjadi nama kurvanya dan yang tercetak di legendanya.
 *
 * Track tetap dibangkitkan meskipun tak satu pun sumurnya membawa mnemonic itu.
 * Kolom-kolom lembar korelasi harus berbaris sama lebar di seluruh panel: garis
 * ikat digambar di atas seluruh lembar sebagai satu overlay, dan sebuah panel
 * yang kehilangan satu kolom menggeser tetangganya sehingga garisnya menunjuk
 * sumur yang salah.
 */
/**
 * Susunan lembar sebelum ada yang menyentuhnya: gamma ray, resistivitas, dan
 * densitas–neutron yang ditumpuk dalam satu kotak — tiga track lembar
 * korelasi cetak. GR membaca litologi, RT fluidanya, dan RHOB–NPHI dalam satu
 * kotak memperlihatkan persilangan keduanya, yang hilang begitu keduanya
 * dipisah ke kotak masing-masing.
 *
 * Dinyatakan sebagai tetapan dan bukan diketik di dua tempat, sebab ia dipakai
 * dua kali dengan arti yang berbeda: sebagai isi lembar yang belum pernah
 * disetel, dan sebagai sasaran tombol yang mengembalikannya. Dua salinan akan
 * membuat "kembalikan ke bawaan" memulangkan sesuatu yang bukan bawaan.
 */
export const CORR_DEFAULT_TRACKS: string[][] = [["GR"], ["RT"], ["RHOB", "NPHI"]];

/**
 * Susunan track lembar: satu larik mnemonic per track.
 *
 * Sampai sekarang yang tersimpan adalah `string[]` — satu mnemonic, satu track,
 * tanpa cara menyatakan dua kurva dalam satu kotak. Bentuk barunya `string[][]`,
 * dan yang lama dibaca sebagai bentuk baru yang setiap tracknya berisi satu
 * mnemonic: itulah arti susunan lama, dinyatakan dalam kosakata yang baru.
 *
 * Yang dibersihkan di sini adalah hal-hal yang membuat lembarnya tidak bisa
 * digambar sama sekali — mnemonic kembar di dua track berbeda, track kosong,
 * dan entri yang bukan teks. Mnemonic kembar dibuang alih-alih dibiarkan sebab
 * setelan kurva disimpan per mnemonic: satu GR di dua kotak adalah satu setelan
 * yang harus melayani dua tempat, dan menyetel salah satunya diam-diam mengubah
 * yang lain.
 */
export function normalizeCorrGroups(raw: unknown): string[][] {
  const masuk: unknown[] = Array.isArray(raw) ? raw : [];
  const dipakai = new Set<string>();
  const hasil: string[][] = [];
  for (const entri of masuk) {
    // Bentuk lama: sebuah mnemonic telanjang, bukan larik.
    const daftar = typeof entri === "string" ? [entri] : Array.isArray(entri) ? entri : [];
    const track: string[] = [];
    for (const name of daftar) {
      if (typeof name !== "string") continue;
      const bersih = name.trim();
      if (!bersih || dipakai.has(bersih)) continue;
      dipakai.add(bersih);
      track.push(bersih);
    }
    // Track tanpa kurva adalah kolom kosong yang tetap memakan lebar dan tetap
    // menggeser kolom di sebelahnya — dan karenanya menggeser garis ikat.
    if (track.length) hasil.push(track);
  }
  return hasil;
}

/** Mnemonic lembar ini, urut sebagaimana tergambar. */
export function groupsToCurves(groups: string[][]): string[] {
  return groups.flat();
}

/**
 * Memindahkan sebuah mnemonic ke dalam track yang memuat mnemonic sasaran.
 *
 * Inilah yang terjadi ketika kepala sebuah kurva dijatuhkan di atas kepala
 * kurva lain: keduanya menjadi satu kotak. Track asal yang kehabisan isi ikut
 * hilang, sebab kolom kosong tetap memakan lebar.
 */
export function mergeIntoTrack(groups: string[][], moved: string, target: string): string[][] {
  if (moved === target) return groups;
  const sasaran = groups.findIndex((g) => g.includes(target));
  if (sasaran < 0) return groups;
  const hasil = groups
    .map((g) => g.filter((name) => name !== moved))
    .map((g, i) =>
      i === sasaran ? [...g.slice(0, g.indexOf(target) + 1), moved, ...g.slice(g.indexOf(target) + 1)] : g,
    )
    .filter((g) => g.length > 0);
  return hasil;
}

/**
 * Mengeluarkan sebuah mnemonic dari tracknya menjadi track sendiri, tepat di
 * sebelah kanan track asalnya.
 */
export function splitFromTrack(groups: string[][], name: string): string[][] {
  const asal = groups.findIndex((g) => g.includes(name));
  if (asal < 0 || groups[asal].length < 2) return groups;
  const sisa = groups[asal].filter((n) => n !== name);
  return [...groups.slice(0, asal), sisa, [name], ...groups.slice(asal + 1)];
}

/**
 * Setelan tampilan satu lembar korelasi, sebagaimana tersimpan di server.
 *
 * Sampai sekarang ketiganya hidup di peramban masing-masing orang: susunan
 * track, lebar spasi, dan setelan tiap mnemonic. Itu benar untuk selera membaca
 * satu orang, tetapi lembar korelasi bukan itu — ia dibaca bersama dan
 * dibicarakan bersama, dan "kotak kedua dari kiri" harus menunjuk kotak yang
 * sama bagi setiap orang yang membukanya. Warna sumur sudah lama hidup di
 * server dengan alasan yang persis sama; ketiga ini menyusul.
 */
export interface CorrDisplay {
  tracks: string[][];
  gap: number;
  curves: CorrCurveStore;
}

/**
 * Membaca setelan lembar dari server, dengan cadangan dari peramban.
 *
 * Cadangannya bukan kemewahan melainkan jalan pindah: seksi yang sudah ada
 * sebelum medan ini lahir memulangkan `{}`, dan yang membacanya sebagai "lembar
 * kosong" akan menghapus susunan yang sudah disetel penggunanya tanpa satu pun
 * kata di layar. Selama server belum punya jawabannya, yang dipakai adalah
 * jawaban peramban — dan sentuhan pertama menuliskannya ke server, tempat
 * seluruh anggota proyek bisa melihatnya.
 */
export function normalizeCorrDisplay(
  raw: unknown,
  fallback: { tracks: string[][]; gap: number; curves: CorrCurveStore },
): CorrDisplay {
  const source = raw && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
  const tracks = "tracks" in source ? normalizeCorrGroups(source.tracks) : fallback.tracks;
  const gap = typeof source.gap === "number" && Number.isFinite(source.gap)
    ? Math.min(1, Math.max(0, source.gap))
    : fallback.gap;
  const curves = "curves" in source ? normalizeCorrCurves(source.curves) : fallback.curves;
  return { tracks, gap, curves };
}

/**
 * Kunci sebuah kurva di dalam track korelasi.
 *
 * Sebuah track kini bisa memuat beberapa mnemonic dari beberapa sumur
 * sekaligus, jadi yang menyebut satu kurva harus menyebut keduanya. Kepala
 * track memulangkan kunci ini apa adanya ketika kurvanya disunting, dan
 * layarnya membacanya kembali untuk tahu setelan mnemonic mana yang berubah.
 *
 * Pemisahnya dua titik dua, sebab mnemonic LAS tidak pernah memuatnya —
 * pemisah yang bisa muncul di dalam nama adalah kunci yang terurai menjadi
 * mnemonic yang salah pada sumur yang salah.
 */
export function curveKey(name: string, wellId: number | string): string {
  return `${name}::${wellId}`;
}

/** Mnemonic dari sebuah `curveKey`; kunci tanpa pemisah dianggap mnemonic. */
export function keyMnemonic(key: string): string {
  const potong = key.indexOf("::");
  return potong < 0 ? key : key.slice(0, potong);
}

/** Id sumur dari sebuah `curveKey`, atau kosong bila bukan kunci majemuk. */
export function keyWellId(key: string): string {
  const potong = key.indexOf("::");
  return potong < 0 ? "" : key.slice(potong + 2);
}

/**
 * Satu track lembar korelasi, dalam bentuk yang dimengerti komponen lembar log.
 *
 * Sebuah track membawa satu mnemonic atau beberapa, masing-masing dari setiap
 * sumur panel ini. Dua sumbu yang harus dibedakan:
 *
 * **Skala mendatar milik mnemonic, bukan milik track.** Dua sumur yang
 * digambar berdampingan pada skala berbeda tidak bisa dibandingkan, dan
 * membandingkannya adalah satu-satunya alasan lembar ini ada — jadi setiap
 * sumur menggambar GR-nya pada skala GR yang sama. Tetapi GR dan VSH yang
 * ditumpuk dalam satu track memang punya skala sendiri-sendiri, sebagaimana di
 * lembar log: 0–150 GAPI dan 0–1 v/v digambar di kotak yang sama, dan yang
 * dibaca dari keduanya adalah bentuknya.
 *
 * **Penggaris dan kisi mengikuti mnemonic pertama.** Satu kotak hanya bisa
 * punya satu deret angka; mencetak dua deret di ruang selebar satu track
 * membuat keduanya tak terbaca. Karena itu mnemonic pertama sebuah track adalah
 * yang angkanya tercetak, dan itu yang membuat urutan di dalam track berarti.
 *
 * Track tetap dibangkitkan meskipun tak satu pun sumurnya membawa mnemonic itu.
 * Kolom-kolom lembar korelasi harus berbaris sama lebar di seluruh panel: garis
 * ikat digambar di atas seluruh lembar sebagai satu overlay, dan sebuah panel
 * yang kehilangan satu kolom menggeser tetangganya sehingga garisnya menunjuk
 * sumur yang salah.
 */
export function corrGroupSpec({
  names,
  wells,
  configs,
  shift,
  wellColor,
  stacked,
  shading,
}: {
  /** Mnemonic track ini, urut sebagaimana tergambar. Yang pertama memegang penggaris. */
  names: string[];
  /** Sumur-sumur panel ini, urut sebagaimana tergambar. */
  wells: PanelWell[];
  /** Setelan per mnemonic. */
  configs: CorrCurveStore;
  /** Geseran kedalaman tiap sumur ke ruang bersama panel — perataan datum. */
  shift: (well: PanelWell) => number;
  /** Warna garis satu sumur pada panel bertumpuk. */
  wellColor: (well: PanelWell, index: number) => string;
  stacked: boolean;
  /** Sakelar arsiran selembar. Mati berarti tidak ada kurva yang diarsir. */
  shading: boolean;
}): TrackSpec {
  const curves: TrackCurve[] = [];
  /**
   * Satuan tiap mnemonic yang benar-benar punya kurva, urut gambar dan tanpa
   * kembar.
   *
   * Sebuah kotak berisi GR dan VSH membawa dua satuan — gAPI dan v/v — dan
   * kepala yang mencetak satuan mnemonic pertama saja mengaku seluruh kotaknya
   * bersatuan gAPI. Yang membaca lembar itu tidak punya cara mengetahui bahwa
   * salah satu kurvanya diukur dengan satuan lain.
   */
  const satuan: string[] = [];
  /** Skala mnemonic pertama yang benar-benar punya kurva — pemegang penggaris. */
  let logTrack = false;
  let spectrumTrack: { name: string; config: SpectrumConfig } | undefined;
  let flexTrack = 1;
  let sudahAdaPenggaris = false;

  names.forEach((name) => {
    const config = configs[name];
    const logarithmic = resolveLogarithmic(name, config);
    const drawn = wells
      .map((well, index) => ({ well, index, curve: well.curves[name] }))
      .filter((entry): entry is { well: PanelWell; index: number; curve: PanelCurve } =>
        Boolean(entry.curve),
      );

    const first = drawn[0]?.curve;
    const [min, max] = resolveAxisRange(
      config?.min ?? first?.display_min ?? 0,
      config?.max ?? first?.display_max ?? 1,
      logarithmic ? "log" : "linear",
    );
    const fill = shading ? resolveFill(name, config) : undefined;
    const customScale = config?.min !== undefined || config?.max !== undefined;

    // GR lebih lebar sedikit: ia kurva yang bentuknya dibaca, bukan angkanya.
    // Sebuah track majemuk selebar yang terlebar di dalamnya, bukan sejumlah
    // keduanya — menumpuk dua kurva justru menghemat lebar, dan track yang
    // melebar setiap kali diisi membatalkan penghematan itu.
    flexTrack = Math.max(flexTrack, name === "GR" ? 1.3 : 1);

    if (first) {
      const unit = first.unit ?? "";
      if (unit && !satuan.includes(unit)) satuan.push(unit);
      if (!sudahAdaPenggaris) {
        sudahAdaPenggaris = true;
        logTrack = logarithmic;
      }
    }

    const spectrum = resolveSpectrum(name, config);
    if (spectrum && !spectrumTrack && drawn.length) {
      spectrumTrack = { name, config: spectrum };
    }

    drawn.forEach(({ well, index, curve }) => {
      const offset = shift(well);
      curves.push({
        // Nama yang tercetak di legenda. Pada panel bertumpuk yang membedakan
        // garis adalah sumurnya; pada track majemuk yang membedakannya adalah
        // mnemonicnya; pada keduanya sekaligus, dua-duanya diperlukan.
        name: stacked ? (names.length > 1 ? `${name} ${well.name}` : well.name) : name,
        // Identitas kurva pada lembar ini adalah pasangan mnemonic dan sumur.
        // Nama tidak bisa memikulnya: pada panel bertumpuk nama kurva adalah
        // nama sumur — yang boleh saja kembar antar sumur — dan pada panel
        // biasa ia mnemonic yang sama untuk setiap kurva di dalam tracknya.
        key: curveKey(name, well.id),
        color: stacked
          ? wellColor(well, index)
          : resolveCorrColor(name, config, curve.color),
        min,
        max,
        unit: curve.unit,
        style: config?.style ?? "solid",
        width: config?.width,
        customScale,
        logarithmic,
        fill,
        // Kedalaman digeser ke ruang bersama panel alih-alih memberi tiap sumur
        // jendela kedalamannya sendiri: satu track adalah satu kotak gambar, dan
        // kotak itu hanya bisa punya satu pasang batas.
        depths: offset ? curve.md.map((md) => md + offset) : curve.md,
        values: curve.values,
      });
    });
  });

  // Pita warna diambil dari mnemonic pertama yang memintanya. Dua pita di satu
  // kotak berarti yang kedua menutupi yang pertama seluruhnya, jadi yang benar
  // bukan menggambar keduanya melainkan memilih satu.
  const spectrumCurve = spectrumTrack
    ? curves.find((c) => keyMnemonic(c.key ?? c.name) === spectrumTrack!.name)
    : undefined;

  return {
    id: names.join("+"),
    title: names.join(" · "),
    units: satuan.join(" · "),
    flex: flexTrack,
    logarithmic: logTrack,
    curves,
    spectrum:
      spectrumTrack && spectrumCurve
        ? {
            curve: spectrumCurve.name,
            ramp: spectrumTrack.config.ramp,
            reverse: spectrumTrack.config.reverse === true,
            lo: spectrumTrack.config.min ?? spectrumCurve.min,
            hi: spectrumTrack.config.max ?? spectrumCurve.max,
            bars: spectrumTrack.config.bars === true,
            custom:
              spectrumTrack.config.min !== undefined || spectrumTrack.config.max !== undefined,
          }
        : undefined,
  };
}

/**
 * Track satu mnemonic — bentuk yang dipakai lembar ini sebelum sebuah track
 * boleh memuat lebih dari satu.
 *
 * Dipertahankan sebagai pembungkus dan bukan disalin: dua jalur yang
 * masing-masing membangun track akan berselisih pada perkara yang tidak
 * kelihatan — ujung skala mana yang menang, arsiran mana yang menyala — dan
 * lembar korelasi tempat satu kolom berperilaku lain dari kolom sebelahnya
 * tidak bisa dibaca mendatar.
 */
export function corrTrackSpec({
  name,
  wells,
  config,
  shift,
  wellColor,
  stacked,
  shading,
}: {
  name: string;
  wells: PanelWell[];
  config?: CorrCurveConfig;
  shift: (well: PanelWell) => number;
  wellColor: (well: PanelWell, index: number) => string;
  stacked: boolean;
  shading: boolean;
}): TrackSpec {
  return corrGroupSpec({
    names: [name],
    wells,
    configs: config ? { [name]: config } : {},
    shift,
    wellColor,
    stacked,
    shading,
  });
}
